"use client";

import Link from "next/link";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";
import { CloseIcon } from "@/components/icons/close.icon";

import type { GetNavigationQueryResult } from "@/sanity.types";

import { HeaderMobileFeaturedProducts } from "./headerMobileFeaturedProducts.component";
import { HeaderMobileSecurityMenu } from "./headerMobileSecurityMenu.component";
import { HeaderMobileSubMenu } from "./headerMobileSubMenu.component";
import {
  hasNavigationLink,
  isNavigationMenuGroupItem,
} from "./navigationQuery.types";

export const HeaderMobileMenu = ({
  menu,
  securityMenu,
}: HeaderMobileMenuProps) => {
  const renderNav = (menu?: GetNavigationQueryResult) => {
    if (!menu?.items?.length) return null;

    return menu.items.map((nav) => {
      if (isNavigationMenuGroupItem(nav)) {
        return <HeaderMobileSubMenu menu={nav} key={nav.label} />;
      }
      if (!hasNavigationLink(nav) || !nav.link.url) return null;
      const target = nav.link.openInNewTab ? "_blank" : "_self";
      return (
        <li
          data-testid="header-main-menu-list-item"
          key={nav.label}
          className="hover:bg-secondary-100 flex duration-500">
          <Link
            className={cn("w-full p-4 whitespace-nowrap")}
            href={{
              pathname: nav.link.url,
              query: nav.link.queryString,
            }}
            target={target}>
            {nav.label}
          </Link>
        </li>
      );
    });
  };
  return (
    <menu
      id="mobile-menu"
      popover="auto"
      className="group/mobile-menu fixed top-0 left-0 z-50 m-0 h-full max-h-none w-90 overflow-hidden border-0 bg-transparent p-0 text-white transition-[overlay,display] transition-discrete duration-500 backdrop:bg-black/50 backdrop:opacity-0 backdrop:transition-[opacity,overlay,display] backdrop:transition-discrete backdrop:duration-500 open:backdrop:opacity-100 lg:hidden starting:open:backdrop:opacity-0 [&>aside]:-translate-x-full [&>aside]:opacity-0 [&>aside]:transition-[transform,opacity] [&>aside]:duration-500 [&>aside]:ease-out open:[&>aside]:translate-x-0 open:[&>aside]:opacity-100 starting:open:[&>aside]:-translate-x-full starting:open:[&>aside]:opacity-0"
      data-testid="header-mobile-menu"
      onClick={(event) => {
        const popover = document.getElementById("mobile-menu");
        if ((event.target as HTMLElement).closest("a")) {
          popover?.hidePopover();
        }
      }}>
      <aside
        className="grid h-full w-90 grid-rows-[72px_1fr]"
        style={{
          background:
            "linear-gradient(161deg, var(--color-secondary, #00226A) 18.9%, var(--color-secondary-100, #07408B) 68.55%, var(--color-primary-200, #5DCDFF) 134.27%)",
        }}>
        <header
          className="flex justify-between px-4 py-3.5"
          style={{
            borderBottom: "2px solid transparent",
            borderImage:
              "linear-gradient(to right, var(--color-secondary), var(--color-primary-200))",
            borderImageSlice: 1,
          }}>
          <Button
            data-testid="header-mobile-menu-Button"
            popoverTarget="header-mobile-security-menu"
            popoverTargetAction="toggle"
            aria-label="Toggle security menu"
            aria-controls="header-mobile-security-menu"
            className={cn(
              "group flex cursor-pointer items-center gap-0.5 whitespace-nowrap open:bg-red-500",
            )}>
            {securityMenu?.label}
            <ChevronDownIcon
              data-testid="header-mobile-menu-chevron"
              className="group-open:rotate-180"
              aria-hidden
            />
          </Button>
          <Button
            data-testid="header-mobile-menu-close-button"
            className="flex h-11 w-11 cursor-pointer items-center justify-center"
            popoverTarget="mobile-menu"
            popoverTargetAction="hide"
            aria-label="Close mobile menu"
            aria-controls="mobile-menu">
            <CloseIcon />
          </Button>
        </header>
        <section className="relative overflow-auto">
          <HeaderMobileSecurityMenu menu={securityMenu} />
          <nav id="main-menu" className="py-6">
            <ul data-testid="header-mobile-menu-list" className="flex flex-col">
              {renderNav(menu)}
            </ul>
          </nav>
          <HeaderMobileFeaturedProducts menu={menu} />
        </section>
      </aside>
    </menu>
  );
};

export type HeaderMobileMenuProps = {
  menu?: GetNavigationQueryResult;
  securityMenu?: GetNavigationQueryResult;
};
